import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNotification } from "@/context/NotificationContext";
import { getCompanyOfferingAccess } from "@/api/getCompanyOfferingAccess/getCompanyOfferingAccessApi";
import { CompanyOfferingAccess } from "@/api/getCompanyOfferingAccess/types";
import {
  clearSubscriptionAccessOverride,
  setSubscriptionAccessOverride,
} from "@/api/manageSubscriptionAccessOverride/manageSubscriptionAccessOverrideApi";

interface CompanySubscriptionAccessProps {
  companyUuid: string;
}

const CompanySubscriptionAccess: React.FC<CompanySubscriptionAccessProps> = ({
  companyUuid,
}) => {
  const { showNotification } = useNotification();
  const [items, setItems] = useState<CompanyOfferingAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState<string | null>(null);

  // Override dialog state
  const [dialogOffering, setDialogOffering] =
    useState<CompanyOfferingAccess | null>(null);
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCompanyOfferingAccess(companyUuid);
      setItems(res.data);
    } catch {
      showNotification("Error", "Failed to load subscription access.", "error");
    } finally {
      setLoading(false);
    }
  }, [companyUuid, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const openOverrideDialog = (item: CompanyOfferingAccess) => {
    setDialogOffering(item);
    setReason(item.overrideReason ?? "");
    // datetime-local wants "YYYY-MM-DDTHH:mm"; trim the ISO string if present.
    setExpiresAt(
      item.overrideExpiresAt ? item.overrideExpiresAt.slice(0, 16) : "",
    );
  };

  // Comp a company into Total Access (LATA) even with no subscription on
  // record — opens the override dialog against a synthetic LATA offering.
  // The backend find-or-creates the override by code, no subscription needed.
  const openGrantTotalAccessDialog = () =>
    openOverrideDialog({
      offeringCode: "LATA",
      offeringName: "Total Access",
      effectiveAccess: false,
      hasOverride: false,
      overrideForceActive: false,
      overrideReason: null,
      overrideExpiresAt: null,
      subscriptions: [],
    });

  const closeDialog = () => {
    setDialogOffering(null);
    setReason("");
    setExpiresAt("");
  };

  const submitOverride = async () => {
    if (!dialogOffering) return;
    setSavingCode(dialogOffering.offeringCode);
    try {
      const res = await setSubscriptionAccessOverride(
        companyUuid,
        dialogOffering.offeringCode,
        {
          forceActive: true,
          reason: reason.trim() === "" ? null : reason.trim(),
          expiresAt:
            expiresAt.trim() === "" ? null : new Date(expiresAt).toISOString(),
        },
      );
      setItems(res.data);
      showNotification("Success", "Access override applied.", "success");
      closeDialog();
    } catch {
      showNotification("Error", "Failed to apply override.", "error");
    } finally {
      setSavingCode(null);
    }
  };

  const removeOverride = async (item: CompanyOfferingAccess) => {
    setSavingCode(item.offeringCode);
    try {
      const res = await clearSubscriptionAccessOverride(
        companyUuid,
        item.offeringCode,
      );
      setItems(res.data);
      showNotification("Success", "Access override removed.", "success");
    } catch {
      showNotification("Error", "Failed to remove override.", "error");
    } finally {
      setSavingCode(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading subscription access…
      </div>
    );
  }

  const hasLataCard = items.some((item) => item.offeringCode === "LATA");

  return (
    <div className="space-y-6">
      {items.map((item) => {
        const anyDelinquent = item.subscriptions.some((s) => s.isDelinquent);
        const isSaving = savingCode === item.offeringCode;
        return (
          <div
            className="rounded-lg border border-gray-200 p-4 dark:border-slate-700"
            key={item.offeringCode}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {item.offeringName}
                </span>
                <span className="text-xs text-gray-400">
                  ({item.offeringCode})
                </span>
              </div>
              <div className="flex items-center gap-2">
                {item.effectiveAccess ? (
                  <Badge variant="success">Access ON</Badge>
                ) : (
                  <Badge variant="secondary">Access OFF</Badge>
                )}
                {anyDelinquent && (
                  <Badge variant="destructive">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    Delinquent
                  </Badge>
                )}
                {item.hasOverride && item.overrideForceActive && (
                  <Badge variant="outline">
                    <ShieldCheck className="mr-1 h-3 w-3" />
                    Override
                  </Badge>
                )}
              </div>
            </div>

            {/* Subscription rows */}
            <div className="mt-3 space-y-1">
              {item.subscriptions.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No subscription on record (access is override-driven).
                </p>
              ) : (
                item.subscriptions.map((sub) => (
                  <div
                    className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                    key={sub.uuid}
                  >
                    {/* "enabled" status and delinquency are already summarized
                        in the top-right badges — only call out a non-enabled
                        (closed-out) sub here to avoid duplicate tags. */}
                    {sub.status !== "enabled" && (
                      <Badge variant="secondary">{sub.status}</Badge>
                    )}
                    <span>{sub.frequency}</span>
                    {sub.startDate && <span>· since {sub.startDate}</span>}
                    {sub.endDate && <span>· ends {sub.endDate}</span>}
                  </div>
                ))
              )}
            </div>

            {/* Override controls */}
            <div className="mt-4 flex items-center gap-2">
              <Button
                disabled={isSaving}
                onClick={() => openOverrideDialog(item)}
                size="sm"
                type="button"
                variant="outline"
              >
                {item.hasOverride ? "Edit override" : "Override access"}
              </Button>
              {item.hasOverride && (
                <Button
                  disabled={isSaving}
                  onClick={() => removeOverride(item)}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Remove override
                </Button>
              )}
              {item.overrideExpiresAt && (
                <span className="text-xs text-gray-400">
                  Expires {new Date(item.overrideExpiresAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {items.length === 0 && (
        <p className="text-sm text-gray-500">
          No subscriptions or access overrides on record for this company.
        </p>
      )}

      {!hasLataCard && (
        <div>
          <Button
            disabled={savingCode !== null}
            onClick={openGrantTotalAccessDialog}
            size="sm"
            type="button"
            variant="outline"
          >
            Grant Total Access override
          </Button>
          <p className="mt-1 text-xs text-gray-400">
            Comp this company into Total Access with no subscription (e.g. while
            billing is sorted out). Creates a force-active override.
          </p>
        </div>
      )}

      <Dialog
        onOpenChange={(open) => !open && closeDialog()}
        open={dialogOffering !== null}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Override access — {dialogOffering?.offeringName}
            </DialogTitle>
            <DialogDescription>
              Force access ON for this offering regardless of delinquency or a
              missing subscription. Leave expiry blank for an indefinite
              override.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="override-reason">Reason</Label>
              <Textarea
                id="override-reason"
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this override being applied? (audit note)"
                value={reason}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="override-expires">Expires (optional)</Label>
              <Input
                id="override-expires"
                onChange={(e) => setExpiresAt(e.target.value)}
                type="datetime-local"
                value={expiresAt}
              />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={closeDialog} type="button" variant="ghost">
              Cancel
            </Button>
            <Button
              disabled={savingCode !== null}
              onClick={submitOverride}
              type="button"
            >
              {savingCode !== null ? "Saving…" : "Apply override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySubscriptionAccess;
